import { useState, useCallback } from 'react';
import { message } from 'antd';
import { customInstance } from '@/shared/api/mutator/custom-instance';
import { Employee, LeaveRequest, LeaveBalance, AttendanceRecord, PayrollEntry } from '../types';

export const useSelfService = () => {
  const [profile, setProfile] = useState<Employee | null>(null);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<LeaveBalance[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [payslips, setPayslips] = useState<PayrollEntry[]>([]);
  const [activePayslip, setActivePayslip] = useState<PayrollEntry | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const data = await customInstance<Employee>({ url: '/api/hr/self-service/me', method: 'GET' });
      setProfile(data);
      return data;
    } catch {
      // User may not have an employee record yet
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyLeaveRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await customInstance<LeaveRequest[]>({ url: '/api/hr/self-service/leave/requests', method: 'GET' });
      setLeaveRequests(data);
    } finally {
      setLoading(false);
    }
  }, []);

  const submitLeaveRequest = useCallback(async (dto: Partial<LeaveRequest>) => {
    const data = await customInstance<LeaveRequest>({
      url: '/api/hr/self-service/leave/requests',
      method: 'POST',
      data: dto,
    });
    message.success('Leave request submitted successfully');
    setLeaveRequests((prev) => [data, ...prev]);
    return data;
  }, []);

  const cancelMyLeaveRequest = useCallback(async (id: number) => {
    const data = await customInstance<LeaveRequest>({
      url: `/api/hr/self-service/leave/requests/${id}`,
      method: 'DELETE',
    });
    message.success('Leave request cancelled');
    setLeaveRequests((prev) => prev.map((r) => (r.id === id ? data : r)));
  }, []);

  const fetchMyLeaveBalances = useCallback(async (year?: number) => {
    setLoading(true);
    try {
      const params = year ? `?year=${year}` : '';
      const data = await customInstance<LeaveBalance[]>({
        url: `/api/hr/self-service/leave/balances${params}`,
        method: 'GET',
      });
      setLeaveBalances(data);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyAttendance = useCallback(async (from?: string, to?: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      const query = params.toString() ? `?${params.toString()}` : '';
      const data = await customInstance<AttendanceRecord[]>({
        url: `/api/hr/self-service/attendance${query}`,
        method: 'GET',
      });
      setAttendance(data);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMyPayslips = useCallback(async () => {
    setLoading(true);
    try {
      const data = await customInstance<PayrollEntry[]>({ url: '/api/hr/self-service/payslips', method: 'GET' });
      setPayslips(data);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPayslip = useCallback(async (id: number) => {
    const data = await customInstance<PayrollEntry>({ url: `/api/hr/self-service/payslips/${id}`, method: 'GET' });
    setActivePayslip(data);
    return data;
  }, []);

  return {
    profile,
    leaveRequests,
    leaveBalances,
    attendance,
    payslips,
    activePayslip,
    loading,
    fetchProfile,
    fetchMyLeaveRequests,
    submitLeaveRequest,
    cancelMyLeaveRequest,
    fetchMyLeaveBalances,
    fetchMyAttendance,
    fetchMyPayslips,
    fetchPayslip,
  };
};
